import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import type { Milestone, MilestonePhase } from "@/hooks/use-milestones";

const PHASES: MilestonePhase[] = ["pre_construction", "construction", "post_construction"];

const PHASE_LABELS: Record<MilestonePhase, string> = {
  pre_construction: "Pre-Con",
  construction: "Construction",
  post_construction: "Closeout",
};

type MilestoneStatus = "on_time" | "completed_late" | "at_risk" | "open";

function getMilestoneStatus(m: Milestone): MilestoneStatus {
  if (m.actualDate) {
    return m.contractDate && m.actualDate > m.contractDate ? "completed_late" : "on_time";
  }
  if (m.forecastDate && m.contractDate && m.forecastDate > m.contractDate) return "at_risk";
  return "open";
}

function worstStatus(statuses: MilestoneStatus[]): MilestoneStatus {
  if (statuses.length === 0) return "open";
  if (statuses.some((s) => s === "completed_late")) return "completed_late";
  if (statuses.some((s) => s === "at_risk")) return "at_risk";
  if (statuses.some((s) => s === "open")) return "open";
  return "on_time";
}

function segmentBg(status: MilestoneStatus): string {
  switch (status) {
    case "on_time":
      return "bg-green-500";
    case "completed_late":
      return "bg-red-500";
    case "at_risk":
      return "bg-amber-400";
    case "open":
      return "bg-gray-300 dark:bg-gray-600";
  }
}

function dotStyle(status: MilestoneStatus): string {
  switch (status) {
    case "on_time":
      return "bg-green-500 border-green-700";
    case "completed_late":
      return "bg-red-500 border-red-700";
    case "at_risk":
      return "bg-amber-400 border-amber-600";
    case "open":
      return "bg-gray-300 border-gray-500 dark:bg-gray-500";
  }
}

function formatDate(d: string | null | undefined): string {
  if (!d) return "--";
  return new Date(d).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

interface Props {
  milestones: Milestone[];
}

export function ProjectProgressBar({ milestones }: Props) {
  const total = milestones.length;
  const completed = milestones.filter((m) => m.actualDate).length;

  if (total === 0) {
    return (
      <div className="mt-3 space-y-1">
        <div className="flex items-center justify-between text-xs text-muted-foreground">
          <span>Milestone Progress</span>
          <span>No milestones</span>
        </div>
        <div className="h-1.5 rounded-full bg-muted" />
      </div>
    );
  }

  const byPhase = Object.fromEntries(
    PHASES.map((p) => [
      p,
      milestones
        .filter((m) => m.phase === p)
        .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0)),
    ]),
  ) as Record<MilestonePhase, Milestone[]>;

  const activePhases = PHASES.filter((p) => byPhase[p].length > 0);

  return (
    <TooltipProvider>
      <div className="mt-3 space-y-1">
        <div className="flex items-center justify-between text-xs text-muted-foreground">
          <span>Milestone Progress</span>
          <span>
            {completed}/{total} complete
          </span>
        </div>

        {/* Segmented bar */}
        <div className="relative flex h-1.5 gap-px overflow-visible">
          {activePhases.map((phase) => {
            const phaseMilestones = byPhase[phase];
            const statuses = phaseMilestones.map(getMilestoneStatus);
            const phaseStatus = worstStatus(statuses);
            const widthPct = (phaseMilestones.length / total) * 100;
            const isFirst = phase === activePhases[0];
            const isLast = phase === activePhases[activePhases.length - 1];

            return (
              <div
                key={phase}
                className={[
                  "relative h-full",
                  segmentBg(phaseStatus),
                  isFirst ? "rounded-l-full" : "",
                  isLast ? "rounded-r-full" : "",
                ].join(" ")}
                style={{ width: `${widthPct}%` }}
              >
                {phaseMilestones.map((m, i) => {
                  const count = phaseMilestones.length;
                  const leftPct = count === 1 ? 50 : (i / (count - 1)) * 100;
                  const status = statuses[i] ?? "on_time";

                  return (
                    <Tooltip key={m.id}>
                      <TooltipTrigger asChild>
                        <span
                          className={[
                            "absolute top-1/2 -translate-x-1/2 -translate-y-1/2",
                            "size-2.5 rounded-full border-2 cursor-default",
                            dotStyle(status),
                          ].join(" ")}
                          style={{ left: `${leftPct}%` }}
                        />
                      </TooltipTrigger>
                      <TooltipContent
                        side="top"
                        className="max-w-52 space-y-0.5 text-left"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <p className="font-semibold">{m.name}</p>
                        <p className="opacity-75">
                          Contract:{" "}
                          <span className="font-medium">{formatDate(m.contractDate)}</span>
                        </p>
                        <p className="opacity-75">
                          Forecast:{" "}
                          <span className="font-medium">{formatDate(m.forecastDate)}</span>
                        </p>
                        <p className="opacity-75">
                          Actual: <span className="font-medium">{formatDate(m.actualDate)}</span>
                        </p>
                      </TooltipContent>
                    </Tooltip>
                  );
                })}
              </div>
            );
          })}
        </div>

        {/* Phase labels */}
        <div className="flex gap-px text-[10px] text-muted-foreground/60">
          {activePhases.map((phase) => {
            const count = byPhase[phase].length;
            const widthPct = (count / total) * 100;
            return (
              <div key={phase} style={{ width: `${widthPct}%` }} className="truncate">
                {PHASE_LABELS[phase]}
              </div>
            );
          })}
        </div>
      </div>
    </TooltipProvider>
  );
}
